import { supabase } from '../lib/supabase';
import { Player, Score } from '../types';

export class ScoringAgent {
  private static instance: ScoringAgent;
  private scoreQueue: Map<string, Score[]> = new Map();
  private processingQueue: boolean = false;
  private lastProcessedVersion: number = 0;
  private version: number = 0;

  private constructor() {}

  public static getInstance(): ScoringAgent {
    if (!ScoringAgent.instance) {
      ScoringAgent.instance = new ScoringAgent();
    }
    return ScoringAgent.instance;
  }

  // Initialize the scoring agent for a tournament
  public async initialize(tournamentId: string): Promise<void> {
    // Load any pending scores from localStorage
    const pendingScores = this.loadPendingScores(tournamentId);
    if (pendingScores.length > 0) {
      this.scoreQueue.set(tournamentId, pendingScores);
      await this.processQueue(tournamentId);
    }

    // Set up real-time subscription
    this.setupRealtimeSubscription(tournamentId);
  }

  // Submit scores for processing
  public async submitScores(tournamentId: string, scores: Score[]): Promise<void> {
    // Add scores to queue
    const currentQueue = this.scoreQueue.get(tournamentId) || [];
    this.scoreQueue.set(tournamentId, [...currentQueue, ...scores]);
    
    // Save to localStorage as backup
    this.savePendingScores(tournamentId, [...currentQueue, ...scores]);
    
    // Process queue if not already processing
    if (!this.processingQueue) {
      await this.processQueue(tournamentId);
    }
  }

  // Process the score queue
  private async processQueue(tournamentId: string): Promise<void> {
    if (this.processingQueue) return;
    this.processingQueue = true;

    try {
      const scores = this.scoreQueue.get(tournamentId) || [];
      if (scores.length === 0) {
        this.processingQueue = false;
        return;
      }

      // Group scores by player
      const scoresByPlayer = new Map<string, Score[]>();
      scores.forEach(score => {
        const playerScores = scoresByPlayer.get(score.playerId) || [];
        scoresByPlayer.set(score.playerId, [...playerScores, score]);
      });

      // Process each player's scores
      for (const [playerId, playerScores] of scoresByPlayer) {
        try {
          // Get current player data
          const { data: playerData, error: fetchError } = await supabase
            .from('players')
            .select('*')
            .eq('id', playerId)
            .single();

          if (fetchError) throw fetchError;

          // Merge scores
          const mergedScores = this.mergeScores(
            playerData.scores || [],
            playerScores,
            playerId,
            tournamentId
          );

          // Calculate total score
          const totalScore = mergedScores.reduce((sum, score) => sum + score.strokes, 0);

          // Update player in database
          const { error: updateError } = await supabase
            .from('players')
            .update({
              scores: mergedScores,
              total_score: totalScore,
              updated_at: new Date().toISOString()
            })
            .eq('id', playerId)
            .eq('tournament_id', tournamentId);

          if (updateError) throw updateError;

          // Remove processed scores from queue
          this.scoreQueue.set(
            tournamentId,
            this.scoreQueue.get(tournamentId)?.filter(
              score => !playerScores.some(s => 
                s.playerId === score.playerId && 
                s.hole === score.hole
              )
            ) || []
          );

          // Update localStorage
          this.savePendingScores(tournamentId, this.scoreQueue.get(tournamentId) || []);
        } catch (error) {
          console.error(`Failed to process scores for player ${playerId}:`, error);
          // Keep failed scores in queue for retry
        }
      }
    } catch (error) {
      console.error('Failed to process score queue:', error);
    } finally {
      this.processingQueue = false;
    }
  }

  // Merge scores with conflict resolution
  private mergeScores(
    existingScores: Score[],
    newScores: Score[],
    playerId: string,
    tournamentId: string
  ): Score[] {
    const mergedScores = new Map<number, Score>();

    // Add existing scores
    existingScores.forEach(score => {
      mergedScores.set(score.hole, score);
    });

    // Merge new scores with conflict resolution
    newScores.forEach(newScore => {
      const existingScore = mergedScores.get(newScore.hole);
      
      if (!existingScore || newScore.timestamp > existingScore.timestamp) {
        mergedScores.set(newScore.hole, newScore);
      }
    });

    return Array.from(mergedScores.values()).sort((a, b) => a.hole - b.hole);
  }

  // Load pending scores from localStorage
  private loadPendingScores(tournamentId: string): Score[] {
    try {
      const storedScores = localStorage.getItem(`pending_scores_${tournamentId}`);
      return storedScores ? JSON.parse(storedScores) : [];
    } catch (error) {
      console.error('Failed to load pending scores:', error);
      return [];
    }
  }

  // Save pending scores to localStorage
  private savePendingScores(tournamentId: string, scores: Score[]): void {
    try {
      localStorage.setItem(`pending_scores_${tournamentId}`, JSON.stringify(scores));
    } catch (error) {
      console.error('Failed to save pending scores:', error);
    }
  }

  // Set up real-time subscription for score updates
  private setupRealtimeSubscription(tournamentId: string): void {
    const channel = supabase
      .channel(`scores_${tournamentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'players',
          filter: `tournament_id=eq.${tournamentId}`
        },
        async (payload) => {
          if (payload.eventType === 'UPDATE') {
            const newPlayer = payload.new as Player;
            // Process any pending scores for this player
            const pendingScores = this.scoreQueue.get(tournamentId)?.filter(
              score => score.playerId === newPlayer.id
            ) || [];
            
            if (pendingScores.length > 0) {
              await this.submitScores(tournamentId, pendingScores);
            }
          }
        }
      )
      .subscribe();
  }

  // Add reset scores method
  public async resetScores(tournamentId: string, playerId: string): Promise<void> {
    try {
      // Get current player data
      const { data: playerData, error: fetchError } = await supabase
        .from('players')
        .select('*')
        .eq('id', playerId)
        .eq('tournament_id', tournamentId)
        .single();

      if (fetchError) throw fetchError;

      // Create reset score entries for each hole
      const resetScores: Score[] = [];
      for (let hole = 1; hole <= 18; hole++) {
        resetScores.push({
          playerId,
          tournamentId,
          hole,
          strokes: 0,
          timestamp: Date.now()
        });
      }

      // Update player in database with empty scores
      const { error: updateError } = await supabase
        .from('players')
        .update({
          scores: [],
          total_score: 0,
          updated_at: new Date().toISOString()
        })
        .eq('id', playerId)
        .eq('tournament_id', tournamentId);

      if (updateError) throw updateError;

      // Clear any pending scores for this player
      const currentQueue = this.scoreQueue.get(tournamentId) || [];
      this.scoreQueue.set(
        tournamentId,
        currentQueue.filter(score => score.playerId !== playerId)
      );

      // Update localStorage
      this.savePendingScores(tournamentId, this.scoreQueue.get(tournamentId) || []);

    } catch (error) {
      console.error('Failed to reset scores:', error);
      throw error;
    }
  }
} 